// classify-agent active card in the Agent tab: the running timeline and the failure state.
// PrecleanSummary renders the Stage A message in the thread.
import { useEffect, useRef, useState } from 'react'
import { CLASS_COLORS } from './utils'

const fmt = n => (n ?? 0).toLocaleString()

function Elapsed({ startedAt }) {
  const [s, setS] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setS(Math.max(0, Math.round((Date.now() - startedAt) / 1000))), 1000)
    return () => clearInterval(t)
  }, [startedAt])
  return <span className="agent-elapsed">{Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')}</span>
}

// The agent's run as a timeline: its (summarized) thoughts in grey italic, its actions as steps.
export function Timeline({ steps, running = false, startedAt, onStop }) {
  const endRef = useRef(null)
  useEffect(() => { if (running) endRef.current?.scrollIntoView({ block: 'nearest' }) }, [steps.length, running])
  const actions = steps.filter(s => s.kind !== 'thought').length
  const lastAction = steps.map(s => s.kind !== 'thought').lastIndexOf(true)
  return (
    <ol className="agent-steps" aria-live={running ? 'polite' : undefined}>
      {steps.map((s, i) => s.kind === 'thought'
        ? <li key={i} className="agent-thought">{s.text}</li>
        : (
          <li key={i} className={`agent-step ${running && i === lastAction ? 'active' : 'done'}`}>
            <span className="agent-step-icon">{running && i === lastAction ? <span className="spin" /> : '✓'}</span>
            <span className="agent-step-text">{s.text}</span>
            <span className="agent-step-t">{Math.round(s.t ?? 0)}s</span>
          </li>
        ))}
      {running && !steps.length && <li className="agent-step active"><span className="spin" /> Starting…</li>}
      {running && (
        <li className="agent-step-foot">
          <span>step {actions} · <Elapsed startedAt={startedAt} /></span>
          {onStop && <button className="btn btn-sm agent-stop" onClick={onStop}>Stop</button>}
        </li>
      )}
      <span ref={endRef} />
    </ol>
  )
}

// Server errors in words the user can act on.
function failureText(error = '') {
  if (/AgentRefused/.test(error)) return 'classify-agent declined this request. Rephrase it and try again.'
  if (/LLM_API_KEY|AgentConfigError/.test(error)) return "classify-agent isn't set up on this server."
  if (/min_tokens|could not classify this drawing/i.test(error))
    return 'The vision model rejected the drawing. Check the drawing units, then classify again.'
  return error
}

function Note({ kind = 'note', children }) {
  return <div className={kind === 'error' ? 'agent-error-banner' : 'agent-note-banner'} role={kind === 'error' ? 'alert' : undefined}>{children}</div>
}

export function PrecleanSummary({ summary, onPreview }) {
  if (summary.note && !summary.dropped)
    return <Note>{summary.note.replace(/^pre-clean /, 'Pre-clean ')}. Classified the full drawing.</Note>
  return (
    <div className="agent-preclean">
      <div className="agent-lead">Hid {fmt(summary.dropped)} entities from the vision model</div>
      {summary.note && <Note>{summary.note}</Note>}
      {Object.entries(summary.by_class ?? {}).map(([cls, layers]) => (
        <div key={cls} className="agent-group">
          <div className="agent-group-head">
            <span className="agent-dot" style={{ background: CLASS_COLORS[cls] }} />{cls}
            <span className="agent-count">{fmt(Object.values(layers).reduce((a, b) => a + b, 0))}</span>
          </div>
          {Object.entries(layers).map(([layer, n]) => {
            const why = summary.ops?.find(o => o.layer === layer && o.to === cls)?.why
            return (
              <button key={layer} className="agent-op-row" onMouseEnter={() => onPreview(cls, layer)}
                onMouseLeave={() => onPreview(null)} onFocus={() => onPreview(cls, layer)} onClick={() => onPreview(cls, layer, true)}>
                <span className="agent-op-line">{layer}<span className="agent-count">{fmt(n)}</span></span>
                {why && <span className="agent-why">{why}</span>}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default function ClassifyAgentBody({ agent, handlers }) {
  if (agent.view === 'running')
    return <Timeline steps={agent.steps ?? []} running startedAt={agent.startedAt} onStop={agent.stage === 'preclean' || agent.stage === 'floor' ? null : handlers.onStop} />
  if (agent.view === 'failed')
    return (
      <div className="agent-failed">
        <Note kind="error">{failureText(agent.error)}</Note>
        {agent.timedOut
          ? <button className="btn agent-apply" onClick={handlers.onRepoll}>Still running on the server — check again</button>
          : <button className="btn agent-apply" onClick={handlers.onRetry}>Retry</button>}
      </div>
    )
  return null
}
